import { Injectable, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { Observable } from 'rxjs';

import { InfoboxService } from '../../common/infobox/infobox.service';

export interface Reservation {
	number: number;
	people: number;
	option: boolean;
	destinationName: string;
	originCity: City;
	amount: number;
	warning?: string;
}
export interface Vehicle {
	id: string;
	name: string;
	seats: number;
	occupied: number;
	crew: number;
	chosen: boolean;
	selected: boolean;
}
export interface City {
	id: string;
	code: string;
	name: string;
	reservations: Array<Reservation>;
	people: number;
	assigned: number;
	options: number;
	x: number;
	y: number;
	primary: boolean;
	domestic: boolean;
}
export interface Transport {
	id: number;
	vehicle: Vehicle;
	city: City;
	domestic: boolean;
}
export interface Action {
	transport: Transport;
	reservation: Reservation;
	action: 'get' | 'put';
}

@Injectable({
	providedIn: 'root',
})
export class TourismMapService {
	transports: Array<Transport> = [];
	transportsChange = new EventEmitter();
	actions: Array<Action> = [];
	actionsChange = new EventEmitter();
	vehicleTable = [];
	vehicleTableChange = new EventEmitter();

	constructor(private readonly infobox: InfoboxService) {}

	driveThru(city: City, vehicle: Vehicle) {
		const cityInRoute = this.transports.find((obj) => obj.city == city && obj.vehicle == vehicle);
		if (cityInRoute) {
			this.infobox.error('To miasto jest już zawarte');
			return;
		}
		this.addTransport(city, vehicle);
		this.vehicleTableRefresh(vehicle);
	}

	getPeople(reservation, city, vehicle: Vehicle): City {
		const free = vehicle.seats - vehicle.occupied - vehicle.crew;
		if (free < reservation.people) {
			this.infobox.error('Nie ma tyle wolnych miejsc');
			return;
		}
		// check if vehicle was in this city
		let transport: Transport = this.transports.find((obj) => obj.vehicle == vehicle && obj.city == city);
		if (!transport) {
			transport = this.addTransport(city, vehicle);
		}
		this.actions.push({
			transport: transport,
			reservation: reservation,
			action: 'get',
		});
		this.actionsChange.emit(this.actions);
		const index = city.reservations.findIndex((obj) => obj === reservation);
		if (index !== -1) {
			city.reservations.splice(index, 1);
			reservation.people = parseInt(reservation.people);
			city.assigned += reservation.people;
			vehicle.occupied += reservation.people;
			if (!city.primary) {
				city.people -= reservation.people;
			}
		}
		this.vehicleTableRefresh(vehicle);
		return city;
	}

	putPeople(action: any, city: City, vehicle: Vehicle) {
		if (action?.left) {
			return;
		}
		const transport: Transport = this.addTransport(city, vehicle);
		this.actions.push({
			transport: transport,
			reservation: action.reservation,
			action: 'put',
		});
		action.left = true;
		this.actionsChange.emit(this.actions);
		vehicle.occupied -= action.reservation.people;
		city.reservations.push(action.reservation);
		city.people += action.reservation.people;
		this.vehicleTableRefresh(vehicle);
	}

	endTrip(destination, vehicle) {
		this.transports.push({
			id: null,
			vehicle: vehicle,
			city: {
				id: destination.stopTo,
				code: destination.id,
				name: destination.name,
				x: 0,
				y: 0,
				reservations: [],
				people: 0,
				assigned: 0,
				options: 0,
				primary: false,
				domestic: false,
			},
			domestic: false,
		});
	}

	driveThruRemove(route, vehicle: Vehicle) {
		const index = this.transports.findIndex((obj) => obj.city == route.city && obj.vehicle == vehicle);
		if (index !== -1) {
			this.transports.splice(index, 1);
			this.transportsChange.emit(this.transports);
		}
		this.vehicleTableRefresh(vehicle);
	}

	private addTransport(city: City, vehicle: Vehicle): Transport {
		const lastTransport: Transport = this.transports[this.transports.length - 1];
		if (lastTransport && lastTransport.vehicle == vehicle && lastTransport.city == city) {
			return lastTransport;
		}
		const transport: Transport = {
			id: null,
			vehicle: vehicle,
			city: city,
			domestic: true,
		};
		this.transports.push(transport);
		this.transportsChange.emit(this.transports);
		return transport;
	}

	vehicleTableRefresh(vehicle: Vehicle) {
		const transports = this.transports.filter((obj) => obj.vehicle.id == vehicle.id);
		this.vehicleTable = [];
		transports.forEach((transport) => {
			const actions = this.actions.filter((obj) => obj.transport == transport);
			if (!actions.length) {
				this.vehicleTable.push({
					city: transport.city,
					action: null,
				});
			}
			actions.forEach((action) => {
				this.vehicleTable.push({
					city: transport.city,
					action: action,
				});
			});
		});
		this.vehicleTableChange.emit(this.vehicleTable);
	}

	undo() {
		// check if transport was last
		const lastTransport: Transport = this.transports[this.transports.length - 1];
		if (!lastTransport) {
			return;
		}
		let lastAction: Action = this.actions[this.actions.length - 1];
		if (!lastAction || lastAction.transport != lastTransport) {
			const vehicle: Vehicle = lastTransport.vehicle;
			this.transports.pop();
			this.transportsChange.emit(this.transports);
			this.vehicleTableRefresh(vehicle);
			return;
		}
		// if actios was last
		const vehicle: Vehicle = lastAction.transport.vehicle;
		switch (lastAction.action) {
			case 'get':
				vehicle.occupied -= lastAction.reservation.people;
				lastTransport.city.reservations.push(lastAction.reservation);
				lastTransport.city.assigned -= lastAction.reservation.people;
				break;
			case 'put':
				const reservation: Reservation =
					lastTransport.city.reservations[lastTransport.city.reservations.length - 1];
				lastTransport.city.people -= reservation.people;
				vehicle.occupied += reservation.people;
				lastTransport.city.reservations.pop();
				break;
		}
		this.actions.pop();
		// if that was last action in this transport, remove transport
		lastAction = this.actions[this.actions.length - 1];
		if (!lastAction || lastAction.transport != lastTransport) {
			this.transports.pop();
		}
		this.actionsChange.emit(this.actions);
		this.transportsChange.emit(this.transports);
		this.vehicleTableRefresh(vehicle);
	}

	reset() {
		this.transports = [];
		this.actions = [];
		this.vehicleTable = [];
	}
}
